import { Toggle } from "@amzn/awsui-components-react";
import TopNavigation from "@amzn/awsui-components-react/polaris/top-navigation";
import { TopNavigationProps } from "@amzn/awsui-components-react/polaris/top-navigation/interfaces";
import { useThemeMode } from "../hooks/useThemeMode";

interface Props {
  setDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function PageHeader({ setDarkMode }: Props) {
  const { checked, handleThemeChange } = useThemeMode(setDarkMode);
  const isDevMode = process.env.NODE_ENV === "development";

  const i18nStrings = {
    overflowMenuTriggerText: "More",
    overflowMenuTitleText: "All",
    overflowMenuBackIconAriaLabel: "Back",
    overflowMenuDismissIconAriaLabel: "Close menu",
  };

  const utilities: TopNavigationProps.Utility[] = [
    {
      type: "menu-dropdown" as const,
      text: "Redrives",
      items: [
        {
          id: "redrive-transformer",
          text: "Redrive Transformer",
          href: "https://beta.console.harmony.a2z.com/lusca-redrives/",
          external: true,
          externalIconAriaLabel: " (opens in a new tab)",
        },
        {
          id: "redrive-tool",
          text: "Redrive Tool",
          href: "/redrive",
        },
        {
          id: "payment-redrive",
          text: "Payment Redrive",
          href: "/payment-redrive",
        },
        {
          id: "dlq-redrive",
          text: "DLQ Redrive",
          href: "/dlq-redrive",
        },
      ],
    },
    {
      type: "menu-dropdown" as const,
      text: "Dashboards",
      items: [
        {
          id: "dlq-dashboard",
          text: "DLQ Dashboard",
          href: "/dashboard",
        },
        {
          id: "uat-dashboard",
          text: "UAT Dashboard",
          href: "/uat",
        },
      ],
    },
    {
      type: "button" as const,
      iconSvg: (
        <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
          <circle
            cx="8"
            cy="8"
            r="7"
            fill={checked ? "#fff" : "#222"}
            stroke="none"
          />
          <path
            d="M8 15C11.866 15 15 11.866 15 8C15 4.134 11.866 1 8 1V15Z"
            fill={checked ? "#222" : "#fff"}
            stroke="none"
          />
        </svg>
      ),
      ariaLabel: "Toggle dark mode",
      onClick: () => handleThemeChange(!checked),
    },
  ];

  return (
    <div
      id="h"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 1000,
        backgroundColor: "white",
      }}
    >
      <TopNavigation
        identity={{
          href: "/",
          title: "Lusca Search Tool",
          logo: {
            src: "data:image/svg+xml;base64,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",
          },
        }}
        i18nStrings={i18nStrings}
        utilities={utilities}
      />
    </div>
  );
}
